import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import { Button } from "../components/ui/button";
import GoogleButton, { OrDivider } from "../components/GoogleButton";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { apiClient } from "../lib/api";
import { SEO } from "../components/seo/SEO";
import { getPageSeo } from "../lib/seoConfig";
import { useIsMobile } from "../components/mobile/DesktopOnlyScreen";

// Technicians land on the mobile home on phones, or on Jobs (desktop-ready)
// on desktop/tablet; everyone else honors a deep-link `from` (but never the
// tech-only /mobile) and otherwise lands on the dashboard.
function destForRole(role, from, isMobile) {
  if (role === "super_admin") return "/platform";
  if (role === "technician") return isMobile ? "/mobile" : "/jobs";
  return from && from !== "/mobile" ? from : "/dashboard";
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);

  // Fetch platform-controlled demo tiles. If the super-admin has the section
  // disabled (default) `accounts` stays empty and nothing renders.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/auth/demo-accounts")
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        if (data.enabled && Array.isArray(data.accounts)) {
          setDemoAccounts(data.accounts);
        }
      })
      .catch(() => {
        // Never block the login page on this — silent failure is fine.
      });
    return () => { cancelled = true; };
  }, []);

  if (user && user !== false) {
    return <Navigate to={destForRole(user.role, location.state?.from, isMobile)} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      navigate(destForRole(u?.role, location.state?.from, isMobile), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  const seo = getPageSeo("/login");
  return (
    <div className="min-h-screen flex bg-slate-50">
      <SEO title={seo.title} description={seo.description} robots={seo.robots} path="/login" />
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-[53px] w-[53px] rounded-xl bg-white flex items-center justify-center overflow-hidden shadow-lg">
              <img src="/brand-logo.png" alt="ServiceGuru Pro" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="text-white font-bold text-[18px] tracking-tight">ServiceGuru Pro</div>
              <div className="text-slate-400 text-[11px] uppercase tracking-wider">Field Ops</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-white text-4xl font-bold leading-tight mb-3">
            Dispatch smarter.<br />
            Diagnose faster.
          </h1>
          <p className="text-slate-300 text-[14px] leading-relaxed">
            Real-time job tracking, AI-powered diagnostics, and geo-aware arrival logs — all in one workspace built for field-service teams.
          </p>
        </div>
        <div className="relative z-10 text-slate-500 text-[11px]">
          © 2026 ServiceGuru Pro · v0.3
        </div>
        {/* subtle grid bg */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-[43px] w-[43px] rounded-lg bg-white flex items-center justify-center overflow-hidden shadow">
              <img src="/brand-logo.png" alt="ServiceGuru Pro" className="h-full w-full object-contain" />
            </div>
            <div className="font-bold text-slate-900">ServiceGuru Pro</div>
          </div>

          <h2 className="text-[26px] font-bold text-slate-900 mb-1.5">Welcome back</h2>
          <p className="text-[13.5px] text-slate-500 mb-6">Sign in to your dispatcher account</p>

          <div className="space-y-3.5 mb-5">
            <GoogleButton label="Continue with Google" testid="login-google-button" />
            <OrDivider />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  autoFocus
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@servicepro.com"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[12px] font-semibold text-slate-700">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[11.5px] text-blue-600 hover:text-blue-700 font-medium">
                  Forgot?
                </Link>
              </div>
              <div className="relative">
                <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-10 pl-9 pr-10 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] font-semibold"
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Signing in…
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <LogIn className="h-4 w-4" /> Sign in
                </span>
              )}
            </Button>
          </form>

          {demoAccounts.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-100">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2.5" data-testid="demo-accounts-heading">Demo accounts</div>
              <div className="space-y-1.5" data-testid="demo-accounts-list">
                {demoAccounts.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    onClick={() => fillDemo(a)}
                    data-testid={`demo-account-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 text-left transition-colors"
                  >
                    <div>
                      <div className="text-[12.5px] font-semibold text-slate-800">{a.label}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{a.email}</div>
                    </div>
                    <div className="text-[11px] text-blue-600 font-medium">Use →</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 text-center text-[12.5px] text-slate-500">
            No account?{" "}
            <Link to="/signup" data-testid="login-register-link" className="text-blue-600 hover:text-blue-700 font-semibold">
              Create one
            </Link>
          </div>

          <div className="mt-4 text-center text-[11.5px] text-slate-400">
            <Link to="/privacy" data-testid="login-privacy-link" className="hover:text-slate-600">Privacy Policy</Link>
            <span className="mx-1.5">·</span>
            <Link to="/terms" data-testid="login-terms-link" className="hover:text-slate-600">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
